import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tab, { TabProps } from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css, FlattenSimpleInterpolation } from "styled-components";

const enum fields {
    showDocuments = "showDocuments",
    showAttributes = "showAttributes",
}

type Props = WidgetProps & HasProduct;
const tabCss: FlattenSimpleInterpolation = css`
    background: ${(props: TabProps) => (props.selected ? "#036EB7" : "transparent")};
    color: ${(props: TabProps) => (props.selected ? "#fff" : "#036EB7")};
    font-weight: ${(props: TabProps) => (props.selected ? "normal" : "bold")};
    border: none;
    padding: 8px 16px;
    z-index: 0;
    &:hover {
        cursor: pointer;
        border: none;
    }
` as FlattenSimpleInterpolation;

const tabContent = css`
    background: #f7fafc;
    body {
        background: #f7fafc;
    }
`;

const tabGroup = css`
    padding: 0;
    margin-top: 20px;
    z-index: 0;
`;

export interface ProductDetailsSpecificationsStyles {
    specificationsAccordion?: AccordionProps;
    specificationsAccordionSection?: AccordionSectionPresentationProps;
    attributeTypeWrapper?: InjectableCss;
    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeLabelWrapper?: InjectableCss;
    tabAttributeTypeLabelText?: DataTableCellProps;
    tabAttributeTypeValuesText?: DataTableCellProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    attributeTypeValueWrapper?: InjectableCss;
    tabsWrapper?: InjectableCss;
    tableWrapper?: DataTableProps;
    documentsContainer?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    tabHeading: InjectableCss;
}

export const specificationsStyles: ProductDetailsSpecificationsStyles = {
    specificationsAccordion: {
        headingLevel: 2,
        css: css`
            margin-bottom: 50px;
            [data-test-selector="sectionHeader"] {
                button[aria-expanded="true"] {
                    background: #036eb7;
                    border-width: 2px;
                    span {
                        color: #fff;
                        letter-spacing: 0.5px;
                    }
                }
                button {
                    background: transparent;
                    border-width: 2px;
                }
            }
            [data-test-selector="sectionPanel"] {
                border-width: 2px;
                background: #f7fafc;
                body {
                    background: transparent;
                }
            }
            ul[class^="UnorderedList"] {
                padding: 0 0 0 15px;
                margin: 5px 0 0 0;
            }
            table tr td:first-child {
                width: auto;
            }
        `,
    },
    attributeTypeLabelText: {
        weight: "bold",
    },
    tabsWrapper: {
        css: css`
            z-index: 0;
            position: relative;
            margin-bottom: 50px;
            ul[class^="UnorderedList"] {
                padding: 0 0 0 10px;
                margin: 0;
            }
            table tr td:first-child {
                width: auto;
            }
        `,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 12],
    },
    documentIcon: {
        src: File,
        color: "text.link",
        css: css`
            margin-right: 10px;
            width: 26px;
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
            width: 137px;
        `,
    },
    attributeTypeWrapper: {
        css: css`
            border: 1px solid #d5d5d5;
            display: flex;
            &:nth-child(even) {
                background: #f7fafc;
            }
        `,
    },
    attributeTypeLabelWrapper: {
        css: css`
            flex-basis: 20%;
            margin-left: 15px;
            display: flex;
            align-items: center;
            height: 100%;
            padding: 6px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 50%;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    attributeTypeValueWrapper: {
        css: css`
            flex-basis: 80%;
            margin-left: 15px;
            display: flex;
            align-items: center;
            height: 100%;
            padding: 6px;
            border-left: 1px solid #d5d5d5;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 50%;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    tableWrapper: {
        css: css`
            width: 100%;
        `,
    },
    tabAttributeTypeLabelText: {
        css: css`
            padding: 0;
            border: none;
            height: auto;
            font-weight: bold;
        `,
    },
    tabAttributeTypeValuesText: {
        css: css`
            padding: 0;
            border: none;
            height: auto;
        `,
    },
    tabHeading: {
        css: css`
            z-index: 0 !important;
        `,
    },
};

const styles = specificationsStyles;

const ProductDetailsSpecifications: React.FC<Props> = ({
    product: { brand, attributeTypes, specifications, documents },
}) => {
    const renderingAttributes = brand || (attributeTypes && attributeTypes?.length > 0);
    const renderingSpecifications = specifications && specifications.length > 0;
    const renderingDocuments = documents && documents.length > 0;
    const specificationsFirst = true;

    if (!renderingAttributes && !renderingSpecifications && !renderingDocuments) {
        return null;
    }

    const specificationsTab = renderingSpecifications ? (
        <StyledWrapper key="Item Features">
            {specifications && (
                <>
                    {specifications && specifications?.length > 0
                        ? specifications?.map(specification => {
                              const tabSpecification = specification || "";
                              return (
                                  <>
                                      <StyledWrapper>
                                          <Typography {...styles.attributeTypeValuesText}>
                                            {parse(transformContentHtml(specification.htmlContent), parserOptions)}
                                          </Typography>
                                      </StyledWrapper>
                                  </>
                              );
                          })
                        : null}
                </>
            )}
        </StyledWrapper>
    ) : null;

    const attributesTab = renderingAttributes ? (
        <StyledWrapper key="Technical Specifications">
            <DataTable {...styles.tableWrapper}>
                <DataTableBody data-test-selector="attributesTable_tableBody">
                    {attributeTypes &&
                        attributeTypes.map(attributeType => (
                            <StyledWrapper {...styles.attributeTypeWrapper} key={attributeType.id.toString()}>
                                <StyledWrapper {...styles.attributeTypeLabelWrapper}>
                                    <Typography {...styles.attributeTypeLabelText}>
                                        {attributeType.label}:&nbsp;
                                    </Typography>
                                </StyledWrapper>
                                <StyledWrapper {...styles.attributeTypeValueWrapper}>
                                    <Typography {...styles.attributeTypeValuesText}>
                                        {attributeType
                                            .attributeValues!.map(attributeValue => attributeValue.valueDisplay)
                                            .join(", ")}
                                    </Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        ))}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    ) : null;

    const documentsTab = renderingDocuments ? (
        <GridContainer {...styles.documentsContainer} key="Resources">
            {documents!.map(document => (
                <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                    <Link
                        href={document.filePath}
                        target="_new"
                        {...styles.documentLink}
                        data-test-selector={`productDetails_${document.id}`}
                    >
                        <IconMemo {...styles.documentIcon} />
                        <Typography {...styles.documentNameText}>{document.name}</Typography>
                    </Link>
                </GridItem>
            ))}
        </GridContainer>
    ) : null;

    const productTabs = [specificationsTab, attributesTab, documentsTab];
    const tabs: any = [];
    productTabs.forEach(productTab => {
        if (productTab) {
            tabs.push(productTab);
        }
    });

    return (
        <>
            {tabs && tabs?.length > 0 ? (
                <Hidden below="md" as="div">
                    <TabGroup cssOverrides={{ tabContent, tabGroup }} {...styles.tabHeading}>
                        {tabs?.map((tabItem: any, index: any) => {
                            return (
                                <Tab css={tabCss} headline={tabItem?.key} tabKey={`tab${index}`}>
                                    {tabItem}
                                </Tab>
                            );
                        })}
                    </TabGroup>
                </Hidden>
            ) : null}
            <Hidden above="sm" as="div">
                <Accordion
                    headingLevel={2}
                    data-test-selector="productDetails_specifications"
                    {...styles.specificationsAccordion}
                >
                    {specificationsFirst}
                    {renderingSpecifications &&
                        specifications?.map((specification, index) => (
                            <ManagedAccordionSection
                                initialExpanded={specificationsFirst || !renderingAttributes || !renderingDocuments}
                                key="Item Features"
                                title="Item Features"
                                data-test-selector="productDetails_specifications_specification"
                                {...styles.specificationsAccordionSection}
                            >
                                {parse(specification.htmlContent, parserOptions)}
                            </ManagedAccordionSection>
                        ))}
                    {renderingAttributes && attributeTypes && attributeTypes?.length > 0 && (
                        <ManagedAccordionSection
                            title={translate("Technical Specification")}
                            initialExpanded={!specificationsFirst || !renderingSpecifications}
                            data-test-selector="productDetails_specifications_attributes"
                            {...styles.specificationsAccordionSection}
                        >
                            {attributeTypes &&
                                attributeTypes.map(attributeType => (
                                    <StyledWrapper {...styles.attributeTypeWrapper} key={attributeType.id.toString()}>
                                        <StyledWrapper {...styles.attributeTypeLabelWrapper}>
                                            <Typography {...styles.attributeTypeLabelText}>
                                                {attributeType.label}:&nbsp;
                                            </Typography>
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.attributeTypeValueWrapper}>
                                            <Typography {...styles.attributeTypeValuesText}>
                                                {attributeType
                                                    .attributeValues!.map(attributeValue => attributeValue.valueDisplay)
                                                    .join(", ")}
                                            </Typography>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ))}
                        </ManagedAccordionSection>
                    )}
                    {renderingDocuments && (
                        <ManagedAccordionSection
                            title={translate("Resources")}
                            data-test-selector="productDetails_specifications_documents"
                            initialExpanded={
                                !!specifications &&
                                specifications.length === 0 &&
                                !!attributeTypes &&
                                attributeTypes.length === 0
                            }
                            {...styles.specificationsAccordionSection}
                        >
                            <GridContainer {...styles.documentsContainer}>
                                {documents!.map(document => (
                                    <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                                        <Link
                                            href={document.filePath}
                                            target="_new"
                                            {...styles.documentLink}
                                            data-test-selector={`productDetails_${document.id}`}
                                        >
                                            <IconMemo {...styles.documentIcon} />
                                            <Typography {...styles.documentNameText}>{document.name}</Typography>
                                        </Link>
                                    </GridItem>
                                ))}
                            </GridContainer>
                        </ManagedAccordionSection>
                    )}
                </Accordion>
            </Hidden>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsSpecifications),
    definition: {
        displayName: "Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showDocuments,
                displayName: "Show Documents",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
        ],
    },
};

export default widgetModule;
